<template>
  <DxPopup :visible="visible" :show-title="true" :title="$t('common.contracts')" :width="'80%'" :height="'85%'" @hiding="onClose">
    <div style="display: flex; flex-direction: column; height: 95%">
      <DxDataGrid
        ref="contractGrid"
        class="contract_grid"
        :data-source="contractsDataSurce"
        :show-borders="true"
        :column-auto-width="true"
        :remote-operations="true"
        :hover-state-enabled="true"
        :show-column-headers="true"
        :repaint-changes-only="true"
        :selected-row-keys="selectedContractUuids"
        @selection-changed="onGridSelectionChanged($event)"
        @row-click="onRowClick"
        :paging="{ pageSize: 25 }"
        :height="'95%'"
      >
        <DxColumn
          dataField="contract_number"
          :caption="$t('stock_management.contracts_purchase_sale.contract_number')"
          :filter-operations="['contains']"
          :allowSorting="false"
          width="15%"
        />
        <DxColumn
          dataField="product_name"
          :caption="$t('common.product')"
          :filter-operations="['contains']"
          :allowSorting="false"
          width="15%"
        />
        <DxColumn
          dataField="legal_entity_name"
          :caption="$t('common.legal_entity_name')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="partner_name"
          :caption="$t('common.partner')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="sale_type"
          :caption="$t('stock_management.contracts_purchase_sale.sale_type')"
          :filter-operations="['=']"
          :allowSorting="false"
          :allowFiltering="!isSaleTypeFilterLocked"
          alignment="center"
          width="10%"
          :calculate-cell-value="getSaleTypeText"
          cell-template="saleTypeTemplate"
        >
          <DxLookup :data-source="saleTypeLookup" value-expr="value" display-expr="text" />
        </DxColumn>
        <template #saleTypeTemplate="{ data }">
          <span>
            {{ getSaleTypeText(data.data) }}
          </span>
        </template>
        <DxColumn
          dataField="contracted_quantity"
          :caption="$t('stock_management.contracts_purchase_sale.contracted_quantity')"
          :filter-operations="['contains']"
          :allowSorting="false"
          dataType="number"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="remaining_quantity"
          :caption="$t('common.remaining_quantity')"
          :filter-operations="['contains']"
          :allowSorting="false"
          :allowFiltering="false"
          dataType="number"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="measure_unit"
          :caption="$t('common.measure_unit')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="notes"
          :caption="$t('common.notes')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="always" selectAllMode="page" />
        <DxFilterRow :visible="true" />
        <DxGrouping :autoExpandAll="true" />
        <DxGroupPanel :visible="false" />
        <DxPaging :total="3500" :page-size="15" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="pageSizes"
          display-mode="compact"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :info-text="$t('common.page_info')"
        />
      </DxDataGrid>
      <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: auto; padding-top: 16px">
        <button class="btn btn-outline-primary col-lg-2 apply-btn" @click="onContinue">{{ $t("common.continue") }}</button>
      </div>
    </div>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  DxDataGrid,
  DxColumn,
  DxSelection,
  DxFilterRow,
  DxGrouping,
  DxGroupPanel,
  DxPaging,
  DxLookup,
  DxPager,
} from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import CustomStore from "devextreme/data/custom_store";

export default {
  name: "ContractsPurchaseSaleSelector",
  components: {
    DxDataGrid,
    DxColumn,
    DxPopup,
    DxSelection,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
    DxLookup,
    DxPager,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      type: Boolean,
      default: () => true,
    },
    selectedContractUuids: {
      type: Array,
      default: () => [],
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      contractsDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };

          const filterData = { ...this.filterData };
          if (Object.keys(filterData).length > 0) {
            Object.assign(payload, filterData);
          }

          try {
            await this.getSmContractsPurchaseSales(payload).then((response) => {
              let rows = Array.isArray(response?.data?.data) ? response.data.data : [];
              dataSource.data = rows;
              dataSource.totalCount = response?.data?.pagination?.total ?? 1;
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching contracts:", error);
            throw error;
          }
        },
        remove: this.removeWeighingOnScale,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
      localselectedContractUuids: [...this.selectedContractUuids],
    };
  },
  computed: {
    ...mapGetters({}),
    isSaleTypeFilterLocked() {
      return !!this.filterData?.sale_type;
    },
    saleTypeLookup() {
      return [
        {
          value: "partner",
          text: this.$t("stock_management.contracts_purchase_sale.sale_type_partner"),
        },
        {
          value: "intragroup",
          text: this.$t("stock_management.contracts_purchase_sale.sale_type_intragroup"),
        },
      ];
    },
  },
  watch: {
    selectedContractUuids: {
      deep: true,
      handler(newData) {
        this.localselectedContractUuids = [...newData];
        this.$refs.contractGrid.instance.selectRows(newData, false);
      },
    },
    filterData: {
      deep: true,
      handler() {
        if (this.$refs.contractGrid?.instance) {
          this.$refs.contractGrid.instance.refresh();
        }
      },
    },
  },
  methods: {
    ...mapActions({
      getSmContractsPurchaseSales: "smContractsPurchaseSale/getSmContractsPurchaseSales",
    }),
    onClose() {
      this.$emit("close");
    },
    onContinue() {
      const selectedRows = this.$refs.contractGrid.instance.getSelectedRowsData();
      this.$emit("continue", selectedRows ?? []); // Emit the selected contracts to the parent component

      this.$refs.contractGrid.instance.clearSelection();
      this.localselectedContractUuids = [];
      this.$emit("update:selectedContractUuids", []);
    },
    onGridSelectionChanged(e) {
      /*if (!this.checkDepartmentRestrictionForSelection(e)) {
        return;
      }*/
      //this.$emit("update:selectedContractUuids", e.selectedRowKeys);
    },
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        return;
      }

      /*if (!this.checkDepartmentRestrictionForSelection(e)) {
        return;
      }*/

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedIndex = this.selectedContractUuids.indexOf(rowKey);

      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.selectedContractUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.selectedContractUuids.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.$refs.contractGrid.instance.selectRows(this.selectedContractUuids, false);
    },
    getSaleTypeText(rowData) {
      const saleType = rowData.sale_type;
      if (saleType === "partner") {
        return this.$t("stock_management.contracts_purchase_sale.sale_type_partner");
      } else if (saleType === "intragroup") {
        return this.$t("stock_management.contracts_purchase_sale.sale_type_intragroup");
      }
      return saleType; // Return the raw value if it doesn't match known types
    },
  },
  mounted() {},
};
</script>
